/**
 * Sprint post-mortem — durable lessons keyed by ending T0.
 * Required before rebaseline / new sprint. Not a second CRM.
 */

import { sprintStartOf, sprintWindow } from './sprint';

export function findPostMortemForSprint(state, sprintStart) {
  const start = String(sprintStart || sprintStartOf(state) || '').slice(0, 10);
  if (!start) return null;
  const rows = state?.sprintReviews || [];
  const matches = rows.filter((r) => String(r.sprintStart || '').slice(0, 10) === start);
  if (!matches.length) return null;
  return [...matches].sort((a, b) =>
    String(b.endedAt || b.updatedAt || '').localeCompare(String(a.endedAt || a.updatedAt || '')),
  )[0];
}

/**
 * Complete when carry-forward + proved/killed + miss + rationale + honest-close are present.
 * Gate option is required unless earlyClose (early rebaseline before Day 45).
 */
export function isPostMortemComplete(row) {
  if (!row) return false;
  const start = String(row.sprintStart || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return false;
  const proved = String(row.proved || '').trim();
  const miss = String(row.biggestMiss || '').trim();
  const rationale = String(row.rationale || '').trim();
  const carry = String(row.carryForward || '').trim();
  const again = row.runAgain === true || row.runAgain === false;
  const againWhy = String(row.runAgainWhy || '').trim();
  if (!proved || !miss || !rationale || !carry || !again || !againWhy) return false;
  if (!row.earlyClose && !String(row.option || '').trim()) return false;
  return true;
}

export function assertCanRebaseline(state, sprintStart) {
  const start = String(sprintStart || sprintStartOf(state) || '').slice(0, 10);
  const pm = findPostMortemForSprint(state, start);
  if (isPostMortemComplete(pm)) return { ok: true, postMortem: pm };
  return {
    ok: false,
    postMortem: pm || null,
    error: start
      ? `Sprint post-mortem for T0 ${start} is required before re-baselining.`
      : 'Sprint post-mortem is required before re-baselining.',
  };
}

/** Prefill from live gateReviews.g45 + sprint bounds. */
export function buildPostMortemFromGate45(state, { today, endedBy = 'day45', earlyClose = false, id } = {}) {
  const sprintStart = sprintStartOf(state);
  const { end } = sprintWindow(sprintStart);
  const g = state?.gateReviews?.g45 || {};
  const existing = findPostMortemForSprint(state, sprintStart);
  return {
    id: id || existing?.id || `spr-${sprintStart}`,
    sprintStart,
    sprintEnd: end,
    endedAt: today || '',
    endedBy,
    earlyClose: !!earlyClose,
    option: g.option || existing?.option || '',
    proved: g.proved || existing?.proved || '',
    disproved: g.disproved || existing?.disproved || '',
    stop: g.stop || existing?.stop || '',
    concentrate: g.concentrate || existing?.concentrate || '',
    biggestMiss: existing?.biggestMiss || '',
    rationale: existing?.rationale || '',
    carryForward: existing?.carryForward || g.concentrate || '',
    runAgain: existing?.runAgain,
    runAgainWhy: existing?.runAgainWhy || '',
    gateSnapshot: {
      option: g.option || '',
      proved: g.proved || '',
      disproved: g.disproved || '',
      stop: g.stop || '',
      concentrate: g.concentrate || '',
      paidReadiness: !!g.paidReadiness,
      bindingPath: !!g.bindingPath,
    },
    decisionId: existing?.decisionId || '',
  };
}

export function emptyGateReviews() {
  return { g15: {}, g30: {}, g45: {} };
}

/** Merge pointer fields onto dual-write sprint settings after rebaseline. */
export function withPostMortemPointer(sprintSettings, { postMortemId, priorSprintStart }) {
  return {
    ...sprintSettings,
    sprint: {
      ...(sprintSettings.sprint || {}),
      lastPostMortemId: postMortemId || '',
      lastPostMortemSprintStart: priorSprintStart || '',
    },
  };
}
